import { computed, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Permission } from '../enums/permission.enum';

import { selectUserPermissions } from '@app/features/auth/store/auth.selectors';
import { Store } from '@ngrx/store';

/**
 * Service to manage and check user permissions with new backend API structure.
 * Handles nested permission modules and maps technical names to display names.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private readonly store = inject(Store);

  // Get flattened permission names directly from selector
  private readonly userPermissions = toSignal(
    this.store.select(selectUserPermissions),
    { initialValue: [] }
  );

  // Create a Set of permission names for fast checking
  private readonly userPermissionsSet = computed(() =>
    new Set(this.userPermissions())
  );

  constructor() {
    effect(() => {
      const permissions = this.userPermissions();
      if (permissions.length === 0) {
        // Permissions not loaded yet or user has no permissions
      }
    });
  }


  /**
   * Check if user has specific permission by technical name
   * @param permission Technical permission name (enum value)
   * @returns True if user has the permission
   */
  hasPermission(permission: Permission): boolean {
    const hasPermission = this.userPermissionsSet().has(permission);
    return hasPermission;
  }

  /**
   * Check if user has at least one of the specified permissions
   * @param permissions Array of technical permission names
   * @returns True if user has any of the permissions
   */
  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some(p => this.hasPermission(p));
  }

  /**
   * Check if user has all of the specified permissions
   * @param permissions Array of technical permission names
   * @returns True if user has all permissions
   */
  hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every(p => this.hasPermission(p));
  }

  /**
   * Convert technical permission name to display name for API checking
   * @param permission Technical name from enum
   * @returns Display name used in API response
   */
  private getDisplayName(permission: Permission): string {
    const mapping: Partial<Record<Permission, string>> = {
      // Authentication
      [Permission.MANAGE_INVITATION_EMAIL]: "Manage Invitation Email",
      [Permission.MANAGE_MFA_SETTINGS]: "Manage MFA Settings",
      [Permission.VIEW_PASSWORD_POLICY]: "View Password Policy",
      [Permission.MANAGE_PASSWORD_POLICY]: "Manage Password Policy",

      // Settings
      [Permission.VIEW_EMAIL_LOGS]: "View Email Logs",
      [Permission.VIEW_EMAIL_TEMPLATES]: "View Email Templates",
      [Permission.MANAGE_EMAIL_TEMPLATES]: "Manage Email Templates",
      [Permission.VIEW_NOTIFICATION_BINDINGS]: "View Email Template Bindings",
      [Permission.MANAGE_NOTIFICATION_BINDINGS]: "Manage Email Template Bindings",
      [Permission.VIEW_TAXES]: "View Taxes",
      [Permission.MANAGE_TAXES]: "Manage Taxes",
      [Permission.VIEW_OPERATION_LOGS]: "View Operation Logs",
      [Permission.VIEW_ENDORSEMENT_FORMS]: "View Endorsement Forms",
      [Permission.MANAGE_ENDORSEMENT_FORMS]: "Manage Endorsement Forms",
      [Permission.ARCHIVE_ENDORSEMENT_FORMS]: "Archive Endorsement Forms",
      [Permission.VIEW_RISK_TYPES]: "View Risk Types",
      [Permission.MANAGE_RISK_TYPES]: "Manage Risk Types",

      // Loss run templates modele
      [Permission.VIEW_LOSS_RUN_TEMPLATES]: "View Loss Run Templates",
      [Permission.MANAGE_LOSS_RUN_TEMPLATES]: "Manage Loss Run Templates",
      [Permission.VIEW_LOSS_RUN]: "View Loss Run Imports",
      [Permission.MANAGE_LOSS_RUN]: "Manage Loss Run Imports",
      [Permission.VIEW_TAGS]: "View Tags",
      [Permission.MANAGE_TAGS]: "Manage Tags",

      // Users and Access
      [Permission.VIEW_USERS_AND_ACCESS]: "View Users and Access",
      [Permission.MANAGE_USERS_AND_ACCESS]: "Manage Users and Access",
      [Permission.MANAGE_TEMPORARY_PASSWORD_FOR_USERS]: "Manage Temporary Password for Users",
      [Permission.ARCHIVE_USERS]: "Archive Users",
      [Permission.UNARCHIVE_USERS]: "Unarchive Users",
      [Permission.VIEW_ROLES]: "View Roles",
      [Permission.MANAGE_ROLES]: "Manage Roles",
      [Permission.ARCHIVE_ROLES]: "Archive Roles",
      [Permission.MANAGE_PROFILE]: "Manage Profile",

      // Products
      [Permission.VIEW_PRODUCTS]: "View Products",
      [Permission.VIEW_ADMIN_PRODUCTS]: "View Admin Products",
      [Permission.MANAGE_ADMIN_PRODUCTS]: "Manage Admin Products",
      [Permission.ARCHIVE_ADMIN_PRODUCTS]: "Archive Admin Products",
      [Permission.VIEW_COVERAGE_TYPES]: "View Coverage Types",
      [Permission.MANAGE_COVERAGE_TYPES]: "Manage Coverage Types",
      [Permission.VIEW_CUSTOM_FIELDS]: "View Attributes",
      [Permission.MANAGE_CUSTOM_FIELDS]: "Manage Attributes",
      [Permission.ARCHIVE_CUSTOM_FIELDS]: "Archive Attributes",

      // Policies
      [Permission.VIEW_POLICIES]: "View Policies",
      [Permission.MANAGE_POLICIES]: "Manage Policies",
      [Permission.ARCHIVE_POLICY]: "Archive Policy",
      [Permission.DELETE_POLICIES]: "Delete Policies",
      [Permission.DELETE_ENDORSEMENT]: "Delete Endorsement",

      // Clients
      [Permission.VIEW_CLIENTS]: "View Clients",
      [Permission.MANAGE_CLIENTS]: "Manage Clients",
      [Permission.ARCHIVE_CLIENT]: "Archive Client",

      // Invoices
      [Permission.VIEW_INVOICES]: "View Invoices",
      [Permission.MANAGE_INVOICES]: "Manage Invoices",

      // Templates (Template Center)
      [Permission.VIEW_TEMPLATES]: "View Templates",
      [Permission.MANAGE_TEMPLATES]: "Manage Templates",
      [Permission.ARCHIVE_TEMPLATES]: "Archive Templates",

      // General Ledger
      [Permission.VIEW_GL_ACCOUNTS]: "View GL Accounts",
      [Permission.MANAGE_GL_ACCOUNTS]: "Manage GL Accounts",
      [Permission.ADMIN_GL_ACCOUNTS]: "Administrate GL Accounts",
      [Permission.MANAGE_GL_BOOKS]: "Manage GL Books",
      [Permission.MANAGE_GL_MAPPING_RULES]: "Manage Posting Mapping Rules",
      [Permission.CREATE_GL_JOURNAL]: "Create Journal Entries",
      [Permission.APPROVE_GL_JOURNAL]: "Approve Journal Entries",
      [Permission.POST_GL_JOURNAL]: "Post Journal Entries",
      [Permission.REVERSE_GL_JOURNAL]: "Reverse Journal Entries",
      [Permission.CLOSE_GL_PERIOD]: "Close Accounting Period",
      [Permission.REOPEN_GL_PERIOD]: "Reopen Accounting Period",
      [Permission.OVERRIDE_GL_RECOGNITION]: "Override Premium Recognition Schedule",
      [Permission.OVERRIDE_CLOSED_PERIOD]: "Override Closed Period Policy",
      [Permission.VIEW_GL_REPORTS]: "View GL Reports",
      [Permission.ADMIN_GL_REPORTS]: "Administrate GL Reports",
      [Permission.MANAGE_AR_INVOICES]: "Manage AR Invoices",
      [Permission.MANAGE_AR_RECEIPTS]: "Manage Cash Receipts",
      [Permission.APPROVE_AR_WRITEOFF]: "Approve AR Write-offs",
      [Permission.MANAGE_AP_BILLS]: "Manage Vendor Bills",
      [Permission.APPROVE_AP_BILLS]: "Approve Vendor Bills",
      [Permission.MANAGE_AP_PAYMENTS]: "Manage AP Payments",
      [Permission.VIEW_BANK_RECON]: "View Bank Reconciliation",
      [Permission.MATCH_BANK_RECON]: "Match Bank Reconciliation",
      [Permission.COMPLETE_BANK_RECON]: "Complete Bank Reconciliation",

      // Approvals
      [Permission.VIEW_APPROVAL_WORKFLOWS]: "View Approval Workflows",
      [Permission.MANAGE_APPROVAL_WORKFLOWS]: "Manage Approval Workflows",
      [Permission.VIEW_APPROVAL_TASKS]: "View Approval Tasks",
      [Permission.MANAGE_APPROVAL_TASKS]: "Manage Approval Tasks",

      // Documents
      [Permission.VIEW_DOCUMENTS]: "View Documents",
      [Permission.MANAGE_DOCUMENTS]: "Manage Documents",

      // Organization Entities
      [Permission.MANAGE_ORGANIZATIONS]: "Manage Organizations",

      // Reporting
      [Permission.VIEW_REPORTING]: "View Reports",
      [Permission.MANAGE_REPORTING]: "Manage Reports",

      // Rating Tables
      [Permission.VIEW_RATING_TABLES]: "View Rating Tables",
      [Permission.MANAGE_RATING_TABLES]: "Manage Rating Tables",
      [Permission.PUBLISH_RATING_TABLES]: "Publish Rating Tables",
      [Permission.ARCHIVE_RATING_TABLES]: "Archive Rating Tables",
      [Permission.DELETE_RATING_TABLES]: "Delete Rating Tables",

      // Claims
      [Permission.VIEW_CLAIMS]: "View Claims",
      [Permission.CREATE_CLAIMS]: "Create Claims",
      [Permission.MANAGE_CLAIMS]: "Manage Claims",
      [Permission.VIEW_SENSITIVE_CLAIMS]: "View Sensitive Claims",
      [Permission.VIEW_ALL_CLAIMS]: "View All Claims",
      [Permission.LINK_CLAIM_POLICY]: "Link Claim to Policy",
      [Permission.BULK_ASSIGN_CATASTROPHE]: "Bulk Assign Claims to Catastrophe Event",
      [Permission.RECLASSIFY_CLAIM_COVERAGE]: "Reclassify Claim Coverage",
      [Permission.RECORD_INVESTIGATION_SUMMARY]: "Record Investigation Summary",
      [Permission.VIEW_LOSS_RUN_REPORT]: "View Loss Run Report",
      [Permission.RECORD_CLAIM_DECISION]: "Record Claim Decision",
      [Permission.MANAGE_CLAIM_RESERVES]: "Manage Claim Reserves",
      [Permission.MANAGE_CLAIM_PAYMENTS]: "Manage Claim Payments",
      [Permission.ISSUE_CLAIM_PAYMENTS]: "Issue Claim Payments",
      [Permission.VOID_CLAIM_PAYMENTS]: "Void Claim Payments",
      [Permission.MANAGE_LITIGATION_BUDGET]: "Manage Litigation Budget",
      [Permission.REOPEN_AGED_CLAIM]: "Reopen Long-Closed Claims",
      [Permission.VIEW_CLAIMS_CONFIGURATION]: "View Claims Configuration",
      [Permission.MANAGE_CLAIMS_CONFIGURATION]: "Manage Claims Configuration",
      [Permission.VIEW_CLAIMS_CONFIGURATION_HISTORY]: "View Claims Configuration History",

      // Notification Settings
      [Permission.VIEW_NOTIFICATION_SETTINGS]: "View Notification Settings",
      [Permission.MANAGE_NOTIFICATION_SETTINGS]: "Manage Notification Settings",

      // Quotes
      [Permission.VIEW_QUOTES]: "View Quotes",
      [Permission.MANAGE_QUOTES]: "Manage Quotes",
      [Permission.CONVERT_QUOTES]: "Convert Quote to Policy",
      [Permission.DELETE_QUOTES]: "Delete Quotes",

      // Captive Management
      [Permission.VIEW_CAPTIVE_MANAGEMENT]: "View Captive Management",
      [Permission.MANAGE_CAPTIVES]: "Manage Captives",
      [Permission.MANAGE_CELLS]: "Manage Cells",
      [Permission.MANAGE_CAPTIVE_CLIENTS]: "Manage Clients",
    };

    return mapping[permission] || permission;
  }
}
